/**
 * NH pilot pulse for Increment 1a profile processing (ITT-972).
 * Not the Inc 1 scoreboard — coverage / open failures / conflicts / retries.
 */

import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { formatBeerProfileProcessingFailureStageLabel } from '../../utils/brewery/beerProfileProcessingFailures';

function formatReportingAsOf(reportingAsOf) {
  if (!reportingAsOf) {
    return '—';
  }
  const dateValue = reportingAsOf instanceof Date
    ? reportingAsOf
    : new Date(reportingAsOf);
  if (Number.isNaN(dateValue.getTime())) {
    return '—';
  }
  return dateValue.toLocaleString();
}

function StageChips({ byStage }) {
  const entries = Object.entries(byStage || {}).filter(([, count]) => Number(count) > 0);
  if (entries.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No open failures by stage
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {entries.map(([stage, count]) => (
        <Chip
          key={stage}
          size="small"
          variant="outlined"
          label={`${formatBeerProfileProcessingFailureStageLabel(stage)}: ${count}`}
        />
      ))}
    </Stack>
  );
}

StageChips.propTypes = {
  byStage: PropTypes.object,
};

export default function BeerProfileProcessingPulseStrip({
  pulse,
  trustedBreweryCount,
  loading,
  error,
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={18} aria-label="Loading NH profile processing pulse" />
        <Typography variant="body2" color="text.secondary">
          Loading NH pilot pulse…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">
          NH pilot pulse (Increment 1a profile processing)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Trusted NH breweries: {trustedBreweryCount}. Conflicts and open failures are
          incomplete — not counted as successful. Not the Inc 1 scoreboard.
        </Typography>
        {pulse.reportingStale ? (
          <Alert severity="warning">
            Failure reporting watermark is older than 5 minutes
            ({formatReportingAsOf(pulse.reportingAsOf)}). Delay detectable (AC6).
          </Alert>
        ) : null}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            color="primary"
            variant="outlined"
            label={`Coverage ${pulse.coveragePercent}% (${pulse.shadowCount}/${pulse.beerCount} shadowed)`}
          />
          <Chip
            color={pulse.completionPercent >= 80 ? 'success' : 'warning'}
            variant="outlined"
            label={`Complete ${pulse.completionPercent}% (excludes conflicts)`}
          />
          <Chip
            color={pulse.openFailures > 0 ? 'error' : 'success'}
            variant="outlined"
            label={`${pulse.openFailures} open failures`}
          />
          <Chip
            color={pulse.conflictCount > 0 ? 'warning' : 'default'}
            variant="outlined"
            label={`${pulse.conflictCount} conflicts`}
          />
          <Chip
            color={pulse.proposedDiffCount > 0 ? 'info' : 'default'}
            variant="outlined"
            label={`${pulse.proposedDiffCount} proposed diffs`}
          />
          <Chip
            variant="outlined"
            label={`${pulse.retryOutcomes} retry outcomes · ${pulse.resolvedFailures} resolved`}
          />
          <Chip
            variant="outlined"
            label={`${pulse.incompleteCount} incomplete`}
          />
        </Stack>
        <StageChips byStage={pulse.byStage} />
        <Typography variant="caption" color="text.secondary">
          Reporting as of: {formatReportingAsOf(pulse.reportingAsOf)}
        </Typography>
      </Stack>
    </Paper>
  );
}

BeerProfileProcessingPulseStrip.propTypes = {
  pulse: PropTypes.shape({
    beerCount: PropTypes.number.isRequired,
    shadowCount: PropTypes.number.isRequired,
    proposedDiffCount: PropTypes.number.isRequired,
    conflictCount: PropTypes.number.isRequired,
    openFailures: PropTypes.number.isRequired,
    resolvedFailures: PropTypes.number.isRequired,
    retryOutcomes: PropTypes.number.isRequired,
    coveragePercent: PropTypes.number.isRequired,
    completionPercent: PropTypes.number.isRequired,
    incompleteCount: PropTypes.number.isRequired,
    reportingAsOf: PropTypes.any,
    reportingStale: PropTypes.bool.isRequired,
    byStage: PropTypes.object,
  }).isRequired,
  trustedBreweryCount: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
};
